import { mapPaginatedResponse, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  PaginatedResponse,
  PharosApiResponse,
  PharosPaginatedResponse,
  Template,
  TemplateWithContent,
} from '@klareospace/pharos-lib-types';

import type { GetTemplatesParams } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TemplateListResult = PaginatedResponse<Template>;

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createTemplateApi(client: ApiClient): {
  getTemplateById: (id: string) => Promise<TemplateWithContent>;
  getTemplates: (params: GetTemplatesParams) => Promise<TemplateListResult>;
} {
  return {
    getTemplateById: async (id: string): Promise<TemplateWithContent> => {
      const response = await client.execute<PharosApiResponse<TemplateWithContent>>(() =>
        client.instance.get(`/templates/${id}`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    getTemplates: async (params: GetTemplatesParams): Promise<TemplateListResult> => {
      const query: Record<string, number | string> = {
        limit: params.pageSize,
        page: params.page,
      };
      const trimmedSearch = params.search?.trim();
      if (trimmedSearch) {
        query['search'] = trimmedSearch;
      }
      if (params.type) {
        query['type'] = params.type;
      }
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<Template>>>(() =>
        client.instance.get('/templates', { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },
  };
}
